import React, { useEffect, useState } from "react";
import { Sparkles, ArrowRight, Download } from "lucide-react";
import { pollCheckoutStatus } from "../../lib/checkout";
import { trackEvent, trackReciprocity } from "../../lib/track";
import { API } from "../../lib/api";
import { markBookPurchased, markBlueprintStarted } from "../../lib/conversionState";
import MagneticButton from "./ui/MagneticButton";

const LOGO = "/assets/logo.png";

const getSessionId = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("session_id");
};

const phaseFromStatus = (data) => {
  if (data.payment_status === "paid") return "paid";
  if (data.status === "expired") return "expired";
  return "pending";
};

const productOf = (data) =>
  (data?.metadata && data.metadata.product) || data?.product || "book";

// ============ Sub-views ============

const BookConfirmation = ({ sessionId }) => {
  useEffect(() => {
    markBookPurchased();
    trackEvent("checkout_success", { product_type: "book", session_id: sessionId });
    trackReciprocity("book_bonus_reflection_prompt", {
      related_session_id: sessionId,
      related_product_type: "book",
    });
    trackEvent("book_bonus_prompt_viewed", { session_id: sessionId });
  }, [sessionId]);

  const downloadUrl = `${API}/downloads/book/${encodeURIComponent(sessionId)}`;

  return (
    <>
      <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
        Your Momentum Has Begun
      </p>
      <h1 className="font-serif text-4xl sm:text-5xl text-themed leading-tight font-light">
        Your copy of Authentic Momentum is ready.
      </h1>
      <p className="mt-5 font-serif italic text-themed-muted text-lg">
        This is more than a download. It is a threshold.
      </p>

      <p className="mt-8 text-themed-muted leading-relaxed">
        You have just stepped into a different relationship with your energy,
        your direction, and the way your life begins to organize when you stop
        forcing what was never aligned.
      </p>
      <p className="mt-4 text-themed-muted leading-relaxed">
        Inside this book, you will find the principles behind moving from
        struggle to flow, from scattered effort to conscious alignment, and
        from survival based motion into authentic momentum.
      </p>
      <p className="mt-4 text-themed-muted leading-relaxed">
        Take your time with it. Read slowly. Let the right lines find you.
      </p>

      <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
        <a
          href={downloadUrl}
          data-testid="book-download-link"
          onClick={() => trackEvent("download_clicked", { product_type: "book", session_id: sessionId })}
          className="btn-gold inline-flex items-center gap-2 justify-center"
        >
          <Download size={14} /> Download the Book
        </a>
        <a
          href="/?checkout=blueprint"
          data-testid="book-to-blueprint-cta"
          className="btn-ghost-gold inline-flex items-center gap-2 justify-center"
        >
          Begin Your Personal Action Plan <ArrowRight size={13} />
        </a>
      </div>

      <p className="mt-6 text-xs text-themed-muted italic max-w-md mx-auto">
        This digital copy is for your personal use only. Please do not
        reproduce, resell, or redistribute it.
      </p>

      {/* Bonus reflection prompt — Reciprocity fulfillment */}
      <div className="mt-14 glass-card p-8 sm:p-10 text-left" data-testid="book-bonus-prompt">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="text-gold" size={18} />
          <p className="text-[10px] tracking-[0.4em] uppercase text-gold/80">
            Your Bonus Reflection Prompt
          </p>
        </div>
        <p className="text-themed-muted leading-relaxed">
          Before you keep moving, pause with this question:
        </p>
        <p className="mt-4 font-serif italic text-2xl text-themed leading-snug">
          Where in my life have I been forcing something that is asking to
          flow?
        </p>
        <p className="mt-5 text-themed-muted text-sm leading-relaxed">
          Journal on it honestly. Do not rush the answer. The first return from
          this book is not information. It is awareness.
        </p>
        <div className="mt-6 border-t border-gold/15 pt-5">
          <p className="text-[10px] tracking-[0.4em] uppercase text-gold/60 mb-2">
            Optional secondary prompt
          </p>
          <p className="font-serif italic text-themed-muted leading-snug">
            What would become available if I stopped trying to prove myself
            through pressure?
          </p>
        </div>
      </div>

      <p className="mt-12 font-serif italic text-themed-muted text-base">
        Your frequency is needed. Step into the flow.
      </p>
    </>
  );
};

const BlueprintConfirmation = ({ sessionId }) => {
  useEffect(() => {
    trackEvent("checkout_success", { product_type: "blueprint", session_id: sessionId });
  }, [sessionId]);

  return (
    <>
      <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
        Your Personal Action Plan Has Opened
      </p>
      <h1 className="font-serif text-4xl sm:text-5xl text-themed leading-tight font-light">
        Your Momentum Personal Action Plan is ready to begin.
      </h1>
      <p className="mt-6 text-themed-muted leading-relaxed">
        Your decision to start is already a signal. The next step is your
        guided clarity questionnaire. Cyrus will personally synthesize your
        complete Personal Action Plan from your answers.
      </p>
      <div className="mt-10 flex justify-center">
        <MagneticButton
          data-testid="blueprint-start-cta"
          onClick={() => {
            window.location.href = `/blueprint/start?session_id=${encodeURIComponent(sessionId)}`;
          }}
          className="btn-gold inline-flex items-center gap-2 justify-center"
        >
          Open My Personal Action Plan <ArrowRight size={13} />
        </MagneticButton>
      </div>
    </>
  );
};

// Clarity Reset Session confirmation. Reached only when the visitor hand-navigates
// to /checkout/success after paying for an Clarity Reset Session (the default
// Stripe success_url routes them straight to /orientation-reset/schedule).
// We surface a single clear path to the scheduler so a misrouted buyer is
// never stranded staring at a Book download button.
const OrientationResetConfirmation = ({ sessionId }) => {
  useEffect(() => {
    trackEvent("checkout_success", {
      product_type: "orientation_reset",
      session_id: sessionId,
    });
  }, [sessionId]);

  const scheduleHref = `/orientation-reset/schedule?session_id=${encodeURIComponent(sessionId)}`;

  return (
    <>
      <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
        Clarity Reset Session · Confirmed
      </p>
      <h1 className="font-serif text-4xl sm:text-5xl text-themed leading-tight font-light">
        Your Clarity Reset Session is paid.
      </h1>
      <p className="mt-6 text-themed-muted leading-relaxed">
        Your $149 secures the seat. The next step is choosing your 30 minute
        window on the scheduler. A confirmation email will land in your inbox
        the moment a time is picked.
      </p>
      <div className="mt-10 flex justify-center">
        <MagneticButton
          data-testid="orientation-reset-success-cta"
          onClick={() => {
            window.location.href = scheduleHref;
          }}
          className="btn-gold inline-flex items-center gap-2 justify-center"
        >
          Continue to Schedule <ArrowRight size={13} />
        </MagneticButton>
      </div>
      <p className="mt-6 text-xs text-themed-muted tracking-wide">
        Session ID · {sessionId?.slice(-12)}
      </p>
    </>
  );
};

const PendingView = () => (
  <>
    <h1 className="font-serif text-4xl sm:text-5xl text-themed leading-tight font-light">
      Your payment is processing.
    </h1>
    <p className="mt-6 text-themed-muted leading-relaxed">
      This usually completes within a moment. Refresh this page to update.
    </p>
  </>
);

const PausedView = () => (
  <>
    <h1 className="font-serif text-4xl sm:text-5xl text-themed leading-tight font-light">
      We could not verify this purchase yet.
    </h1>
    <p className="mt-6 text-themed-muted leading-relaxed">
      If your payment was completed, give it a moment and refresh. Your access
      is protected, not lost. If this persists, reach out via the consultation
      form.
    </p>
  </>
);

const CheckingView = () => (
  <h1 className="font-serif text-4xl sm:text-5xl text-themed leading-tight font-light">
    Confirming your alignment…
  </h1>
);

const CheckoutSuccess = () => {
  const sessionId = getSessionId();
  const [state, setState] = useState({ phase: "checking", data: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "missing", data: null });
      return;
    }
    pollCheckoutStatus(sessionId, { maxAttempts: 10, intervalMs: 2000 })
      .then((data) => setState({ phase: phaseFromStatus(data), data }))
      .catch(() => setState({ phase: "error", data: null }));
  }, [sessionId]);

  const product = productOf(state.data);

  let content;
  if (state.phase === "checking") content = <CheckingView />;
  else if (state.phase === "paid" && product === "blueprint") content = <BlueprintConfirmation sessionId={sessionId} />;
  else if (state.phase === "paid" && product === "orientation_reset") content = <OrientationResetConfirmation sessionId={sessionId} />;
  else if (state.phase === "paid") content = <BookConfirmation sessionId={sessionId} />;
  else if (state.phase === "pending") content = <PendingView />;
  else content = <PausedView />;

  return (
    <div
      data-testid="checkout-success-page"
      className="min-h-screen bg-themed text-themed relative overflow-hidden flex items-center justify-center px-6"
    >
      <div className="absolute inset-0 gold-glow-radial pointer-events-none" />
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      <div className="relative max-w-2xl text-center py-20">
        <div className="flex justify-center mb-10">
          <img
            src={LOGO}
            alt="Authentic Momentum"
            className="w-24 h-24 object-contain logo-mask animate-breathe-premium"
            draggable="false"
          />
        </div>

        {content}

        {state.phase !== "paid" && (
          <div className="mt-12 flex justify-center">
            <MagneticButton
              data-testid="checkout-success-home"
              onClick={() => (window.location.href = "/")}
              className="btn-gold inline-flex items-center gap-2 justify-center"
            >
              Return Home <ArrowRight size={13} />
            </MagneticButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
